import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { appRoutes, RouteObject } from './routes.types';
import { useAuthStore } from 'src/services/Auth/Auth.store';
import { usePreLoginAuthLocation } from 'src/routes/use-pre-login-auth-location';
import { useSaveHubspotUtmLocationEffect } from 'src/lib/services/hubspot/use-hubspot-saved-utm-location';
import { useAuth } from 'src/services/Auth';
import { useProfile } from 'src/models';
import { useBrandsQuery } from 'src/api/queries';
import { usePendingPurchaseEvents } from 'src/hooks/use-pending-purchase-events';
import { isMobileAppOnboardingAllowed, isReactNativeWebView } from 'src/utils/webview-detection';

type ProtectedRouteProps = {
  route: RouteObject;
};

// TODO: This component should only be rendered once per route.
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ route }) => {
  const location = useLocation();
  const { isLoggedIn } = useAuthStore();
  const { rememberLocation } = usePreLoginAuthLocation();
  const { refresh } = useAuth();
  // Enable profile query when user is logged in - this is critical for redirect logic
  const profile = useProfile({ enabled: isLoggedIn });
  const brandsQuery = useBrandsQuery();
  const hasBrands = !!brandsQuery?.data?.items?.length;

  useSaveHubspotUtmLocationEffect();

  // Process pending purchase events when user is authenticated
  usePendingPurchaseEvents(isLoggedIn);

  useEffect(() => {
    // Invoke refresh token every hour
    const tokenRefreshInvertal = setInterval(() => refresh(), 60 * 60 * 1000);

    return () => {
      clearInterval(tokenRefreshInvertal);
    };
  }, [refresh]);

  // Wait for initial data to load before evaluating any redirect conditions
  const isInitiallyLoading =
    (isLoggedIn && !profile.isLoaded) ||
    (isLoggedIn && profile.query.isLoading) ||
    (isLoggedIn && brandsQuery.isLoading && !brandsQuery.isFetched);

  if (route.underMaintenance) {
    return <Navigate to={appRoutes.maintenance} />;
  }

  // Wait for queries to load, but proceed if they error out to avoid trapping users
  if (isInitiallyLoading && !profile.query.isError && !brandsQuery.isError) {
    return (
      <route.layout>
        <div />
      </route.layout>
    );
  }

  // Redirect non-logged-in users to login page
  if (!isLoggedIn && route.restricted?.includes('guest')) {
    if (
      location.pathname === appRoutes.addSubscriptionPlanSelect ||
      location.pathname === appRoutes.stripePaymentLinkRedirect
    ) {
      return (
        <Navigate to={appRoutes.register} state={{ from: rememberLocation(location) }} replace />
      );
    }
    return <Navigate to={appRoutes.login} state={{ from: rememberLocation(location) }} replace />;
  }

  // Check if this is mobile app onboarding flow (allows bypassing subscription)
  const isMobileOnboardingRoute = location.pathname === appRoutes.mobileOnboardingCreateBrand;
  const isMobileRequestEditRoute = location.pathname === appRoutes.mobileRequestEdit;
  const isMobileAppAllowed =
    (isMobileOnboardingRoute && isMobileAppOnboardingAllowed()) || isMobileRequestEditRoute;

  // Redirect users without active subscriptions to subscription page
  // Exception: Mobile app onboarding flow and impersonated users are allowed to bypass this
  if (
    profile.isLoaded &&
    !profile.query.isLoading &&
    !profile.hasActiveSubscriptions &&
    !profile.isImpersonated &&
    !isMobileAppAllowed &&
    location.pathname !== appRoutes.addSubscriptionPlanSelect &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect
  ) {
    return <Navigate to={appRoutes.addSubscriptionPlanSelect} state={{ from: location }} replace />;
  }

  // Force brand creation for new users (no brands AND no requests)
  // Users with requests but no brands can access dashboard (edge case: deleted brand)
  //
  // `!brandsQuery.isError` is load-bearing, not defensive noise. `hasBrands` reads
  // `data?.items?.length`, which an ERRORED query leaves undefined — and `isFetched` is true
  // after a failure — so without it a failed brands request is indistinguishable from "this
  // customer owns no brands", and a paying customer with brands gets sent into first-brand
  // onboarding on one transient 401/429. "We don't know" is not "none". Line 54 already
  // exempts isError from the loading gate for the same reason; this branch was the one place
  // that still conflated them.
  if (
    profile.isLoaded &&
    !brandsQuery.isLoading &&
    brandsQuery.isFetched &&
    !brandsQuery.isError &&
    profile.hasActiveSubscriptions &&
    !hasBrands &&
    !profile.hasRequests &&
    location.pathname !== appRoutes.createBrand &&
    location.pathname !== appRoutes.dashboard &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect &&
    location.pathname !== appRoutes.mobileRequestEdit
  ) {
    return <Navigate to={appRoutes.createBrand} state={{ from: location }} replace />;
  }

  if (isLoggedIn) {
    const userType = AuthUtils.getPayload()?.user_type;

    // Redirect logged-in users away from restricted pages (login/register)
    if (route.restricted?.includes(userType as any) && (hasBrands || profile.hasRequests)) {
      // In mobile WebView, redirect to AI agent instead of dashboard
      const defaultRoute = isReactNativeWebView() ? appRoutes.aiAgent : appRoutes.dashboard;
      return <Navigate to={defaultRoute} replace />;
    }

    // Redirect users with failed subscription payments to account hold page
    // Exception: Impersonated users should bypass this to allow admins to view the account
    if (
      profile.isLoaded &&
      !profile.query.isLoading &&
      !profile.isImpersonated &&
      !profile.eligibleForTrial &&
      !profile.hasActiveSubscriptions &&
      profile.hasFailedSubscriptions &&
      location.pathname !== appRoutes.accountOnHold &&
      location.pathname !== appRoutes.addSubscriptionPlanSelect &&
      !location.pathname.includes('/subscription-cancelation/subscription')
    ) {
      return <Navigate to={appRoutes.accountOnHold} replace />;
    }

    // Redirect from account hold page to dashboard if subscription is active
    if (
      profile.isLoaded &&
      !profile.query.isLoading &&
      !brandsQuery.isLoading &&
      location.pathname === appRoutes.accountOnHold &&
      !profile.hasFailedSubscriptions &&
      hasBrands
    ) {
      const defaultRoute = isReactNativeWebView() ? appRoutes.aiAgent : appRoutes.dashboard;
      return <Navigate to={defaultRoute} replace />;
    }
  }

  return (
    <route.layout>
      <Outlet />
    </route.layout>
  );
};
